/**
 * CLOUDWEB-21080: чтение состояния устройства в Service Worker.
 *
 * Рекорд `device` в `cloud-sw-device` пишет страница (`webPush.deviceState` в page context),
 * читает воркер. Через него до воркера доезжает то, чего он про устройство знать не может:
 * открыто ли приложение прямо сейчас.
 *
 * Исход попытки открыть нативку сюда не пишется: воркеру он не нужен, а нужен аналитику — и
 * уезжает радаром со страницы. Запись в базу была бы write-only.
 *
 * Контракт как у `omicronConfig` и `claimPushId`: наружу не всплывает ни одна ошибка IDB.
 * Нет записи, недоступна база, битый рекорд — везде `null`, то есть вызывающий ведёт себя так,
 * будто задачи CLOUDWEB-21080 не было.
 */
import { DEVICE_STATE_KEY, ensureStore, SW_DEVICE_STATE_DB } from 'serviceWorker/swIdbSchema';

export interface DeviceStateRecord {
    /** Когда страница в последний раз отмечалась видимой. Свежесть метки = «приложение открыто». */
    visibleAt?: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

/** Открытие базы с мемоизацией соединения — по образцу `omicronConfig.ts`. */
function openDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') {
        return Promise.resolve(null);
    }

    if (dbPromise) {
        return dbPromise;
    }

    dbPromise = new Promise((resolve) => {
        try {
            const request = indexedDB.open(SW_DEVICE_STATE_DB.name, SW_DEVICE_STATE_DB.version);

            request.onupgradeneeded = () => {
                ensureStore(request.result, SW_DEVICE_STATE_DB);
            };

            request.onsuccess = () => {
                const db = request.result;

                // Не держим соединение, если схему апгрейдит другой контекст: иначе его open
                // зависнет на onblocked. Кэш сбрасываем — переоткроемся при следующем обращении.
                db.onversionchange = () => {
                    db.close();
                    dbPromise = null;
                };

                resolve(db);
            };
            request.onerror = () => {
                // Кэш сбрасываем: единичный сбой открытия не должен закрепить `null` навсегда.
                dbPromise = null;
                resolve(null);
            };
        } catch {
            dbPromise = null;
            resolve(null);
        }
    });

    return dbPromise;
}

/**
 * Прочитать рекорд состояния устройства.
 *
 * Значение не кэшируется, в отличие от `getSwOmicronConfig`: маяк меняется постоянно, и
 * закэшированный `visibleAt` означал бы решение по устаревшим данным. Мемоизируется только
 * соединение с базой.
 */
export async function readDeviceState(): Promise<DeviceStateRecord | null> {
    try {
        const db = await openDb();

        if (!db) {
            return null;
        }

        return await new Promise<DeviceStateRecord | null>((resolve) => {
            try {
                const request = db
                    .transaction(SW_DEVICE_STATE_DB.store, 'readonly')
                    .objectStore(SW_DEVICE_STATE_DB.store)
                    .get(DEVICE_STATE_KEY);

                request.onsuccess = () => {
                    const record = request.result as DeviceStateRecord | undefined;

                    resolve(record && typeof record === 'object' ? record : null);
                };
                request.onerror = () => resolve(null);
            } catch {
                resolve(null);
            }
        });
    } catch {
        return null;
    }
}
